import { type AssetsManifest, type Container, type Renderer } from 'pixi.js';
import { MagicLayout } from '@magic/layout';
import { ParseAmount } from 'stake-engine';
import { settingsStore } from '../stores/settings.store';
import { getGameConfig } from 'config/game.config';
import { RoundController } from 'controllers/stateControllers/Round.controller';
import { ReelController } from 'controllers/stateControllers/Reel.controller';
import { BalanceController } from 'controllers/Balance.controller';
import { RulesPopupController } from 'controllers/RulesPopup.controller';
import { PopupController, type PopupResult } from 'controllers/Popup.controller';
import { SymbolImagesController } from 'controllers/SymbolImages.controller';
import {
    isIdle,
    setGameState,
    apiStore,
    getSpinMode,
    getBuyMode,
    getBuyCost,
} from '../stores/api.store';
import type { APIController, RoundData } from '../utils/api.types';
import { findSpineBundles } from '../utils/spineBundles';
import { clearFreeSpinsProgress } from '../utils/freeSpinsStorage';

export class MainController {
    spine: MagicLayout;
    private roundController: RoundController;
    private reelController: ReelController;
    private balanceController: BalanceController;
    private popupController: PopupController;
    /** Both present only when the game config declares a rules page. */
    rulesPopup?: RulesPopupController;
    symbolImages?: SymbolImagesController;
    private spinInFlight = false;
    private endRoundInFlight: Promise<void> | null = null;
    /** The buy bonus offer is on screen, waiting for the player to take it. */
    private buyOfferOpen = false;

    constructor(
        private manifest: AssetsManifest,
        private globalView: Container,
        private api: APIController,
        /** Draws the symbol art the rules page shows — see {@link SymbolImagesController}. */
        private renderer: Renderer,
    ) {
        this.spine = new MagicLayout({
            debug: settingsStore.getState().spineDebug,
            skipAttachingSpinesPatterns: ['symbol'],
        });

        const { names, folder } = findSpineBundles(this.manifest);
        const bundles = this.manifest.bundles.filter((bundle) => names.includes(bundle.name));

        this.spine.createInstancesFromManifest({ bundles }, folder);

        this.globalView.addChild(this.spine);

        this.reelController = new ReelController(this.spine);
        this.popupController = new PopupController(this.spine);
        this.roundController = new RoundController(
            this.spine,
            this.reelController,
            this.popupController,
            (index) => this.recordRoundEvent(index),
        );
        this.balanceController = new BalanceController(this.api);

        const { rules } = getGameConfig();
        if (rules) {
            // started here, at the top of the game, so the art is ready long before the info
            // button can be pressed — and only for a game that has a page to put it on
            this.symbolImages = new SymbolImagesController(this.spine, this.renderer);
            this.rulesPopup = new RulesPopupController(
                this.spine,
                this.popupController,
                rules,
                this.symbolImages,
            );
        }

        this.addSubscriptions();
        this.balanceController.start();
        setInterval(() => this.spine.texts.set('time', this.formatTime()), 1000);
    }

    /**
     * Wall clock as HH:MM. Built by hand rather than through toLocaleTimeString
     * so the field always gets 24-hour, zero-padded, colon-separated text — the
     * locale otherwise decides the hour cycle and the separator.
     */
    private formatTime() {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private addSubscriptions() {
        apiStore.subscribe(
            (state) => state.state,
            (state, previousState) => {
                if (previousState === 'loading' && state === 'idle') {
                    this.startGame();
                }

                if (state === 'spin') {
                    this.roundController.setTotalWin('-');
                }
            },
        );

        apiStore.subscribe(
            (state) => state.balance,
            (balance) => balance && this.roundController.setBalance(balance),
        );

        apiStore.subscribe(
            (state) => state.betAmount,
            (betAmount) => this.roundController.setBet(betAmount),
        );

        this.roundController.setBet(apiStore.getState().betAmount);

        apiStore.subscribe(
            (state) => state.round,
            async (round) => {
                if (!round) return;

                const roundData = round as unknown as RoundData;

                await this.roundController.playState(roundData);

                // The presentation is over — the player has seen the result, so
                // settle the round now. The payout is only credited by EndRound,
                // so this is what puts the win in the wallet and, through the
                // response balance, on screen while the win is still displayed.
                // Deferring it to the next spin click (as a winning round used
                // to be) leaves the balance showing the pre-payout amount for as
                // long as the player looks at it, and the credit then lands
                // milliseconds before the next bet deduction overwrites it.
                //
                // The round stays open for the whole sequence, so a refresh
                // mid-win still replays it; only a refresh after the sequence
                // has finished now starts from a clean board, which is correct —
                // that win is paid.
                //
                // Only settle while this round is still the current one: a spin
                // clicked during the sequence tail (finalWin holds ~1s after
                // going idle) settles this round itself and may already have a
                // NEW round active — settling then would end that fresh round
                // mid-book (e.g. right as free spins start).
                if (apiStore.getState().round?.betID === round.betID) {
                    await this.settleRound();
                }
            },
        );

        this.spine.animations.addEventListener('spin_click', async () => this.onSpinClick());

        this.spine.animations.addEventListener('shop_click', async () => this.onShopClick());

        this.spine.animations.addEventListener('bet_minus_click', async () => {
            apiStore.getState().actions.betDown();
        });

        this.spine.animations.addEventListener('bet_plus_click', async () => {
            apiStore.getState().actions.betUp();
        });

        // On `down`, not `click`: the mute toggle is two overlapping buttons, and the
        // press animation slides the pressed one out of the way to reveal its opposite.
        // Its hit areas hang off the bone that moves, so once the swap has happened the
        // pointer is no longer over the button — Pixi then reports the release as
        // `pointerupoutside` (`up_out`) and never fires `click`. Any press held past the
        // swap would leave the setting untouched while the button already looks toggled:
        // sound stayed muted under an unmuted-looking button. Committing on the same
        // interaction that commits the visual keeps the two from disagreeing.
        this.spine.animations.addEventListener('mute_down', () =>
            settingsStore.setState({ muted: true }),
        );

        this.spine.animations.addEventListener('unmute_down', () => {
            settingsStore.setState({ muted: false });
            // Releases a state applied on load: it holds its end pose on its own
            // track, and the click animation doesn't pose the bone and slots that
            // state owns, so the button would stay looking muted. `resetPose` is
            // off — the UI skeleton is shared with the rest of the game, which
            // must not be snapped back to its setup pose.
            this.spine.animations.stopState('muted', false);
        });

        // The mute states are sent on load and nowhere else: the click animations
        // carry the button into its new look and hold it there, so replaying a
        // state on every setting change would fight the click mid-press. What the
        // load does need is the *persisted* setting — the skeleton always loads in
        // its unmuted setup pose, so a player who muted last session has it
        // applied once, up front. Only when muted: unmuted *is* the setup pose,
        // and playing its state would flash the button's click frame.
        if (settingsStore.getState().muted) {
            this.spine.animations.playState('muted');
        }
    }

    private onSpinStart() {
        this.roundController.onSpinStart();
        // solo, so any lingering symbol state (active/inactive) is cleared
        this.spine.animations.playByName('blur', true);
        // loops the spin animation (filling random symbols on update/symbol-X)
        // until the round result triggers roundController -> reelController.reveal()
        this.reelController.spin();
    }

    private async onSpinClick() {
        await this.startSpin(getSpinMode());
    }

    /**
     * Opens the buy bonus offer and plays the feature if the player takes it.
     *
     * Both the mode played and the multiplier it is priced at come from the
     * authenticate response, so the shop only ever sells what the RGS says this
     * game sells, at the price it charges for it.
     *
     * The offer is the one popup with a real choice in it, so it is the one shown
     * with both buttons: the labelled one buys the feature, the corner X walks away.
     */
    private async onShopClick() {
        const gameMode = getBuyMode();

        if (!gameMode) {
            console.warn('No buyable game mode was reported for this game');
            return;
        }

        // the offer is priced at the bet it was opened on, and taking it places a
        // spin — so it can only open when a spin could start, and only once
        if (this.buyOfferOpen || !this.canStartSpin()) return;

        this.buyOfferOpen = true;

        let taken: PopupResult;

        try {
            taken = await this.popupController.show({
                layout: 'close_and_accept',
                title: `buy bonus`,
                content: ParseAmount(getBuyCost(gameMode)).toFixed(2),
                // the price was settled before the popup opened — nothing to count up to
                animateContent: false,
                buttonText: `buy`,
            });
        } finally {
            this.buyOfferOpen = false;
        }

        // closing the offer is turning it down: nothing is charged and nothing spins
        if (taken !== 'accept') return;

        // the popup held the player for as long as they looked at it, so the
        // spin re-checks the game state rather than trusting the earlier one
        await this.startSpin(gameMode.mode);
    }

    /** A player-initiated spin can start: none is in flight and free spins aren't running. */
    private canStartSpin(): boolean {
        return !this.spinInFlight && isIdle() && !this.roundController.freeSpinsActive;
    }

    /**
     * Places a spin at the current bet in the given mode and hands the round to
     * the presentation. Shared by the spin button and the buy bonus offer, which
     * differ only in the mode they play — and so in what the RGS charges for it.
     *
     * The mode is a plain string: the spin button plays one of this game's own
     * (`SpinMode`), while a bought feature plays whatever name the RGS gave it.
     */
    private async startSpin(mode: string) {
        // spinInFlight covers the async gap before setGameState('spin') — the
        // game state alone lets a double-click send play twice
        if (!this.canStartSpin()) {
            return;
        }
        this.spinInFlight = true;

        this.spine.texts.set('win', ''); // reset win text on each spin start
        this.spine.texts.set('total_win', ''); // reset total win text on each spin start

        try {
            const amount = apiStore.getState().betAmount;

            // close the previous round before playing the next one. It was
            // already settled when its sequence finished, so this normally
            // resolves at once (or waits on that request if it is still in
            // flight) — it only has real work to do when the player clicked
            // through the tail of the sequence, before the settle got there
            await this.settleRound();

            if (amount) {
                // TODO: inverstigate if we need to use this state
                setGameState('spin');
                this.onSpinStart();

                // the bet is always the plain bet level — a bought mode is priced
                // by the RGS, which charges it at the mode's cost multiplier
                const spinResponce = await this.api.spin(amount, mode);

                if (spinResponce) {
                    apiStore.getState().actions.setPlayResponse(spinResponce);
                    // the play response is the newest balance there is — drop any
                    // poll still in flight, it answered with the pre-bet value
                    this.balanceController.apply(spinResponce.balance);
                    setGameState('reveal');
                } else {
                    await this.abortSpin();
                }
            }
        } catch (error) {
            console.error('Failed to start spin:', error);
            await this.abortSpin();
        } finally {
            this.spinInFlight = false;
        }
    }

    /**
     * Ends the still-open backend round, crediting its payout. Every round is
     * settled as soon as its sequence finishes, so the next spin click doesn't
     * have to pay for an EndRound request first. The in-flight request is
     * shared: a spin clicked during the tail of the sequence awaits the same
     * promise instead of sending EndRound a second time.
     */
    private settleRound(): Promise<void> {
        if (apiStore.getState().roundActive) {
            this.endRoundInFlight ??= (async () => {
                try {
                    // the payout is credited on EndRound, so its response holds
                    // the balance the player ends the round with — the play
                    // response only ever had the bet deducted
                    const response = await this.api.endRound();

                    this.balanceController.apply(response.balance);
                } catch (error) {
                    // a failed EndRound must not block the game: if the round is
                    // genuinely still open, the play request's round-already-active
                    // fallback ends it again
                    console.error('Failed to end round:', error);
                }

                apiStore.getState().actions.markRoundEnded();

                // the round is closed, so there is nothing left to replay or
                // resume — progress only ever covers a round still open on the
                // backend (a free spins round now stays open until this point,
                // one spin click after its last reveal)
                clearFreeSpinsProgress();

                this.endRoundInFlight = null;
            })();
        }

        return this.endRoundInFlight ?? Promise.resolve();
    }

    /**
     * Confirms a played book event index to the backend (`/bet/event`).
     * Fire-and-forget: progress tracking must never block or fail the
     * animation sequence.
     */
    private recordRoundEvent(index: number) {
        Promise.resolve(this.api.event(String(index))).catch((error) =>
            console.error('Failed to record round event:', error),
        );
    }

    /** Settles the reels without a result and returns to idle after a failed spin request. */
    private async abortSpin() {
        await this.reelController.stop();
        setGameState('idle');
    }

    private async startGame() {
        const { balance, round } = apiStore.getState();

        if (balance?.amount && !round) {
            this.reelController.fillBoardWithRandomSymbols();
        }
    }

    // TODO: move this to spineLayout or create separate SpineResizer utility
    resize(w = window.innerWidth, h = window.innerHeight) {
        this.spine.x = w / 2;
        this.spine.y = h / 2 + 20;

        const { minHeight, minWidth } = getGameConfig();

        let scaleX = 1;
        let scaleY = 1;

        if (w < minWidth) {
            scaleX = w / minWidth;
        }

        if (h < minHeight) {
            scaleY = h / minHeight;
        }

        this.spine.scale.set(Math.min(scaleX, scaleY));
    }
}
